import { ChatbotQueryFilter } from "./chatbot.types";

// All patterns run on normalised text (lowercase, diacritics stripped).

const RE_FUGAS =
  /\bfugas?\b|\bwater\s+leaks?\b|\bleak\s+detection\b|\bleaks?\s+(?:greater|more|bigger)\b/;
// Leak sensors belong to the sensorica screens, not to the leaks query.
const RE_SENSOR = /\b(?:sensor\w*|capteurs?)\b/;

const RE_PARADOS =
  /\b(?:contadores?|medidores?)\s+parados?\b|\bmeters?\s+(?:that\s+have\s+)?stopped\b|\b(?:se\s+)?han\s+parados?\b|\bdejado\s+de\s+(?:comunicar|funcionar)\b/;
// Without a period these phrases mean the "no communication" device list.
const RE_SIN_COMUNICACION = /\bsin\s+comunicacion\b|\bwithout\s+communication\b|\bno\s+signal\b/;

const RE_CONSUMO_NEGATIVO = /\bconsumo\s+negativo\b|\bnegative\s+consumption\b|\bconsommation\s+negative\b/;

const RE_PICOS_ANOMALOS =
  /\bpicos?\s+anomalos?\b|\bcontadores?\s+con\s+picos?\b|\babnormal\s+(?:consumption\s+)?peaks?\b|\bpic\s+anormal\b|\bpicchi\s+anomali\b/;

const RE_COMPARACION =
  /\bcompar(?:acion|ar|ativa)\s+(?:de\s+)?(?:\w+\s+)?(?:consumos?|periodos?)\b|\bcompar(?:ison|e)\s+(?:of\s+)?(?:consumption|periods?)\b/;

const RE_VALORES_INICIALES =
  /\bvalor(?:es)?\s+inicial(?:es)?\b|\bcontadores?\s+instalados?\b|\binitial\s+(?:meter\s+)?values?\b|\bvaleurs?\s+initiales?\b/;

const RE_CONSUMO_HORA =
  /\bconsumo\s+(?:por\s+)?hora(?:rio)?\b|\bhourly\s+consumption\b|\bcontadores?\s+por\s+(?:hora|consumo)\b|\bmeters?\s+by\s+(?:hourly\s+)?consumption\b/;

const RE_THRESHOLD =
  /(?:mas\s+de|mayor(?:es)?\s+(?:de|que)|superior(?:es)?\s+a(?:l)?|more\s+than|greater\s+than)\s+(\d+(?:[.,]\d+)?)|(\d+(?:[.,]\d+)?)\s*(?:litros?|liters?|l\/h)\b/;

/**
 * Extracts a numeric consumption threshold ("mas de 15 litros", "more than 15").
 * Returns 0 when absent (= any consumption > 0).
 */
export function extractConsumptionThreshold(normalized: string): number {
  const m = RE_THRESHOLD.exec(normalized);
  if (!m) { return 0; }
  const val = Number.parseFloat((m[1] ?? m[2]).replace(",", "."));
  return Number.isNaN(val) ? 0 : val;
}

/**
 * Detects the query preset (analisis-datos/consultas) requested by the message.
 * `hasDate` lets ambiguous phrases ("sin comunicacion") pick the date-based query.
 * autoExecute is only set for read-only queries (see CHATBOT_ALLOWED_AUTO_EXECUTE).
 */
export function detectQueryFilter(normalized: string, hasDate = false): ChatbotQueryFilter | null {
  if (RE_FUGAS.test(normalized) && !RE_SENSOR.test(normalized)) {
    // Query 4: meters ALWAYS consuming > X (hydraulic leak signature).
    return {
      profile: "fugas",
      queryId: 4,
      inputValues: { consumo: extractConsumptionThreshold(normalized) },
      autoExecute: true
    };
  }
  if (RE_PARADOS.test(normalized) || (hasDate && RE_SIN_COMUNICACION.test(normalized))) {
    return { profile: "parados", queryId: 2, autoExecute: true };
  }
  if (RE_CONSUMO_NEGATIVO.test(normalized)) {
    return { profile: "consumo-negativo", queryId: 3, autoExecute: true };
  }
  if (RE_PICOS_ANOMALOS.test(normalized)) {
    return { profile: "picos-anomalos", queryId: 5, autoExecute: false };
  }
  if (RE_COMPARACION.test(normalized)) {
    return { profile: "comparacion", queryId: 9, autoExecute: false };
  }
  if (RE_VALORES_INICIALES.test(normalized)) {
    return { profile: "valores-iniciales", queryId: 10, autoExecute: true };
  }
  if (RE_CONSUMO_HORA.test(normalized)) {
    return {
      profile: "consumo-hora",
      queryId: 1,
      inputValues: { consumo: extractConsumptionThreshold(normalized) },
      autoExecute: false
    };
  }
  return null;
}
